import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useSelector } from "react-redux";

import { useManagedMembersContext } from "@/context/ManagedMembersContext";
import { getMemberInfo } from "@/hooks/useMembers";
import vitalsTrackingService from "@/services/vitals-tracking.service";
import { RootState } from "@/store";
import { MemberType } from "@/types/member";
import { VitalsParameter, VitalsSwitchMember } from "@/types/vitals";

import AddReadingModal from "./AddReadingModal";

export interface AddVitalFlowProps {
  open: boolean;
  onClose: () => void;
}

/**
 * AddVitalFlow
 * Entry point for the FAB "Log a vital" action. Opens AddReadingModal
 * directly, defaulting to the logged-in member and blood pressure; the
 * modal's own member/vital-type switchers handle changing the selection.
 */
const AddVitalFlow: React.FC<AddVitalFlowProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { managedMembers, fetchManagedMembers } = useManagedMembersContext();
  const loggedInMember = getMemberInfo(true);
  const selectedMember = useSelector(
    (state: RootState) => state.auth.selectedMember
  );

  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [selectedParameter, setSelectedParameter] = useState<VitalsParameter>(
    VitalsParameter.BP
  );
  const [isFirstTime, setIsFirstTime] = useState(false);

  const checkFirstTime = useCallback(
    async (memberId: string, parameter: VitalsParameter) => {
      try {
        const config = await vitalsTrackingService.getTrackingConfig(memberId);
        setIsFirstTime(!config.enabledParameters?.includes(parameter));
      } catch {
        setIsFirstTime(true);
      }
    },
    []
  );

  useEffect(() => {
    if (!open || !loggedInMember?.id) return;
    // Prefer the currently selected (non-pet) member from Redux; fall back to logged-in member
    const defaultId =
      selectedMember?.id && selectedMember.member_type !== MemberType.PET
        ? selectedMember.id
        : loggedInMember.id;
    setSelectedMemberId(defaultId);
    setSelectedParameter(VitalsParameter.BP);
    fetchManagedMembers();
    checkFirstTime(defaultId, VitalsParameter.BP);
  }, [
    open,
    loggedInMember?.id,
    selectedMember?.id,
    fetchManagedMembers,
    checkFirstTime,
  ]);

  const sortedMembers = useMemo(() => {
    return managedMembers
      .filter((m) => m.member_type !== MemberType.PET)
      .sort((a, b) => {
        if (a.id === loggedInMember?.id) return -1;
        if (b.id === loggedInMember?.id) return 1;
        return (a.first_name || "")
          .toLowerCase()
          .localeCompare((b.first_name || "").toLowerCase());
      });
  }, [managedMembers, loggedInMember?.id]);

  const switchableMembers: VitalsSwitchMember[] = useMemo(
    () =>
      sortedMembers.map((m) => {
        const isSelf = m.id === loggedInMember?.id;
        return {
          id: m.id,
          name: isSelf ? t("common.you") : m.nickname || m.first_name || "",
          first_name: m.first_name,
          last_name: m.last_name,
          nickname: isSelf ? t("common.you") : m.nickname,
          avatar: m.avatar,
        };
      }),
    [sortedMembers, loggedInMember?.id, t]
  );

  const handleMemberChange = async (memberId: string) => {
    setSelectedMemberId(memberId);
    await checkFirstTime(memberId, selectedParameter);
  };

  const handleParameterChange = async (parameter: VitalsParameter) => {
    setSelectedParameter(parameter);
    if (selectedMemberId) await checkFirstTime(selectedMemberId, parameter);
  };

  if (!open || !selectedMemberId) return null;

  return (
    <AddReadingModal
      key={`${selectedMemberId}-${selectedParameter}`}
      isOpen={open}
      onClose={onClose}
      onSuccess={onClose}
      parameter={selectedParameter}
      memberId={selectedMemberId}
      isFirstTime={isFirstTime}
      members={switchableMembers}
      onMemberChange={handleMemberChange}
      onParameterChange={handleParameterChange}
    />
  );
};

export default AddVitalFlow;
